/**
 * The one place the paper workspaces keep anything in the browser.
 *
 * What a paper row shows (status, score, mode) comes from the server
 * (POST /paper_attempts/statuses). The browser only keeps a copy of the
 * signed-in user's scores, so a row can show them before the server answers,
 * plus that user's target and ESAT optional-module choices. Every value is
 * stored under its base key and the user's id, and is read only with that id:
 * no user id, nothing is read or written. The essay-feedback panels' marker
 * of a run in flight is kept the same way, in this tab's sessionStorage.
 * Logout, the end of a session and a sign-in as another account clear every
 * key here for every user (clearPaperProgressCaches), because a shared
 * school computer must not keep one student's progress for the next.
 *
 * Before this module the workspaces stored the same values under the bare
 * base keys, shared by every account on the browser, and kept a
 * `completed_<exam>_quizzes` list per exam. Those legacy keys are deleted on
 * first load (purgeLegacyPaperProgressCaches) and never read.
 */

const PAPER_EXAMS = ['lnat', 'tmua', 'esat', 'ucat', 'tara'];

/** Every base key the score utils store under, before the user id. */
export const PAPER_PROGRESS_BASE_KEYS = [
  ...PAPER_EXAMS.map((exam) => `${exam}_paper_scores`),
  ...PAPER_EXAMS.map((exam) => `${exam}_workspace_target`),
  'esat_sitting_chosen_optionals',
];

// The per-exam completed lists the page wrote for every exam it showed,
// not only the five above, so they are matched by shape.
const LEGACY_COMPLETED_KEY = /^completed_.+_quizzes$/;

/** Between the base key and the user id in every per-user key. */
export const USER_SEPARATOR = '::user::';

/**
 * The essay-feedback panels' marker of a scoring run in flight
 * (sessionStorage), so a reload in the same tab can resume the wait. It names
 * an attempt of the signed-in user, so it is kept under that user's id
 * (readUserSession / writeUserSession) and goes on logout too.
 */
export const LNAT_FEEDBACK_IN_FLIGHT_KEY = 'lnat_feedback_in_flight';
export const TARA_FEEDBACK_IN_FLIGHT_KEY = 'tara_feedback_in_flight';
const FEEDBACK_IN_FLIGHT_KEYS = [LNAT_FEEDBACK_IN_FLIGHT_KEY, TARA_FEEDBACK_IN_FLIGHT_KEY];

function usableUserId(userId) {
  if (userId == null) return null;
  const text = String(userId).trim();
  return text ? text : null;
}

/** The storage key for one user's value, or null when there is no user. */
export function userCacheKey(baseKey, userId) {
  const id = usableUserId(userId);
  if (!id) return null;
  return `${baseKey}${USER_SEPARATOR}${id}`;
}

/**
 * The user's parsed value, or `fallback` when there is no user, nothing is
 * stored, or the stored text is not JSON of the expected kind.
 */
export function readUserCache(baseKey, userId, fallback) {
  const key = userCacheKey(baseKey, userId);
  if (!key) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return fallback;
    return parsed;
  } catch {
    return fallback;
  }
}

/**
 * Store the user's value. Without a user nothing is written and false is
 * returned. A storage error (full, or blocked in private mode) is thrown to
 * the caller, as localStorage.setItem throws it.
 */
export function writeUserCache(baseKey, userId, value) {
  const key = userCacheKey(baseKey, userId);
  if (!key) return false;
  localStorage.setItem(key, JSON.stringify(value));
  return true;
}

/** The user's value in this tab's sessionStorage, or null (no user, nothing, bad JSON). */
export function readUserSession(baseKey, userId) {
  const key = userCacheKey(baseKey, userId);
  if (!key) return null;
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

/**
 * Store the user's value in this tab's sessionStorage. Without a user
 * nothing is written and false is returned; a storage error is thrown.
 */
export function writeUserSession(baseKey, userId, value) {
  const key = userCacheKey(baseKey, userId);
  if (!key) return false;
  sessionStorage.setItem(key, JSON.stringify(value));
  return true;
}

/** Forget the user's value in this tab's sessionStorage. */
export function removeUserSession(baseKey, userId) {
  const key = userCacheKey(baseKey, userId);
  if (key) sessionStorage.removeItem(key);
}

function storageKeys(storage) {
  const keys = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key != null) keys.push(key);
  }
  return keys;
}

function isLegacyKey(key) {
  return PAPER_PROGRESS_BASE_KEYS.includes(key) || LEGACY_COMPLETED_KEY.test(key);
}

function isUserKey(key, baseKeys = PAPER_PROGRESS_BASE_KEYS) {
  const at = key.indexOf(USER_SEPARATOR);
  return at > 0 && baseKeys.includes(key.slice(0, at));
}

// The in-flight markers, unkeyed (before they carried the user id) or any
// user's.
const isInFlightKey = (key) => FEEDBACK_IN_FLIGHT_KEYS.includes(key) || isUserKey(key, FEEDBACK_IN_FLIGHT_KEYS);

function removeMatching(storage, predicate) {
  let removed = 0;
  storageKeys(storage).forEach((key) => {
    if (!predicate(key)) return;
    storage.removeItem(key);
    removed += 1;
  });
  return removed;
}

// Both run on paths that must finish (app start, logout and session
// expiry), so a storage error is logged and 0 is returned.

/** Delete the unkeyed keys every account on the browser shared. */
export function purgeLegacyPaperProgressCaches(storage = localStorage, session = sessionStorage) {
  try {
    removeMatching(session, (key) => FEEDBACK_IN_FLIGHT_KEYS.includes(key));
  } catch (err) {
    console.error('legacy feedback in-flight marker purge failed', err?.name || null);
  }
  try {
    return removeMatching(storage, isLegacyKey);
  } catch (err) {
    console.error('legacy paper progress purge failed', err?.name || null);
    return 0;
  }
}

/**
 * Delete every paper progress key, legacy and per-user, for every user, and
 * the feedback panels' in-flight markers.
 */
export function clearPaperProgressCaches(storage = localStorage, session = sessionStorage) {
  try {
    removeMatching(session, isInFlightKey);
  } catch (err) {
    console.error('feedback in-flight marker clear failed', err?.name || null);
  }
  try {
    return removeMatching(storage, (key) => isLegacyKey(key) || isUserKey(key));
  } catch (err) {
    console.error('paper progress cache clear failed', err?.name || null);
    return 0;
  }
}

/** One user's paperId -> { correct, total, mode, updatedAt } map; {} without a user. */
export function loadPaperScores(baseKey, userId) {
  return readUserCache(baseKey, userId, {});
}

/** Store one paper's score for the user. Without a user or paper id nothing is written. */
export function savePaperScore(baseKey, userId, paperId, { correct, total, mode } = {}) {
  if (!paperId || !usableUserId(userId)) return;
  const all = loadPaperScores(baseKey, userId);
  all[String(paperId)] = {
    correct: Number(correct) || 0,
    total: Number(total) || 0,
    mode: mode || all[String(paperId)]?.mode || null,
    updatedAt: Date.now(),
  };
  writeUserCache(baseKey, userId, all);
}

/** Forget these papers' cached scores for the user (a retake). */
export function clearPaperScores(baseKey, userId, paperIds = []) {
  const all = loadPaperScores(baseKey, userId);
  let changed = false;
  (paperIds || []).filter(Boolean).forEach((id) => {
    if (all[String(id)]) {
      delete all[String(id)];
      changed = true;
    }
  });
  if (changed) writeUserCache(baseKey, userId, all);
}
